import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import digitalTwinApi from '@/services/apiService/digitalTwin';
export default {
  name: 'ReferenceIds',
  props: {
    value: {
      type: Array,
      required: true
    },
    defaultKeyName: {
      type: String,
      default: 'Key'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    dense: {
      type: Boolean,
      default: false
    },
    singleColumn: {
      type: Boolean,
      default: false
    },
    keyLabel: {
      type: String,
      required: true
    },
    valueLabel: {
      type: String,
      required: true
    },
    type: {
      type: String,
      default: 'digitalTwin',
      validator: value => ['digitalTwin', 'virtualDevice'].includes(value)
    },
    tenantId: {
      type: Number,
      default: null
    },
    requiredValueByKey: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      referenceIdPairs: [],
      forbiddenCharacters: ['"', '\\', '{', '}', '[', ']', ':', ','],
      loading: false,
      linkedReferenceIds: [],
      linkedTwinHeaders: [{
        text: this.keyLabel || this.$i18n_t('DigitalTwin.DigitalTwin.dataRouting.referenceIdName'),
        value: 'name',
        width: '20%',
        align: 'start',
        sortable: false
      }, {
        text: this.valueLabel || this.$i18n_t('DigitalTwin.DigitalTwin.dataRouting.referenceIdValue'),
        value: 'value',
        width: '20%',
        align: 'start',
        sortable: false
      }, {
        text: this.$i18n_t('DigitalTwin.DigitalTwin.dataRouting.addressedDTs'),
        value: 'linkedTwins',
        width: '50%',
        align: 'start',
        sortable: false
      }, {
        text: '',
        value: 'actions',
        width: '10%',
        align: 'end',
        sortable: false
      }]
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'color']),
    classVerticalSpace() {
      return this.dense ? 'py-1' : 'py-2';
    },
    hasNoEntries() {
      return this.referenceIdPairs.length === 0;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        this.referenceIdPairs = newVal || [];
        this.updateLinkedReferenceIds();
      }
    },
    referenceIdPairs: {
      deep: true,
      handler() {
        this.updateLinkedReferenceIds();
      }
    }
  },
  mounted() {
    this.updateLinkedReferenceIds();
  },
  methods: {
    validateKey(value) {
      if (!value) {
        return false;
      }
      for (const char of this.forbiddenCharacters) {
        if (value.includes(char)) {
          return false;
        }
      }
      return true;
    },
    isValueRequired(key) {
      if (!this.requiredValueByKey || !key) {
        return true;
      }
      const ruleConfig = this.requiredValueByKey[key];
      if (ruleConfig === undefined) {
        return false;
      }
      if (ruleConfig && typeof ruleConfig === 'object') {
        return !!ruleConfig.requiredValue;
      }
      return !!ruleConfig;
    },
    validateValue(value, index) {
      const key = this.entryKey(index);
      const isRequired = this.isValueRequired(key);
      if (!value) {
        return !isRequired;
      }
      for (const char of this.forbiddenCharacters) {
        if (value.includes(char)) {
          return false;
        }
      }
      return true;
    },
    entryKey(index) {
      return Object.keys(this.referenceIdPairs[index])[0];
    },
    entryValue(index) {
      return Object.values(this.referenceIdPairs[index])[0];
    },
    updateKey(index, newKey) {
      const value = Object.values(this.referenceIdPairs[index])[0];
      this.$set(this.referenceIdPairs, index, {
        [newKey]: value
      });
      this.emitUpdate();
    },
    updateValue(index, newValue) {
      const key = Object.keys(this.referenceIdPairs[index])[0];
      this.$set(this.referenceIdPairs, index, {
        [key]: newValue
      });
      this.emitUpdate();
    },
    addEntry(index = -1) {
      const insertIndex = index >= 0 ? index + 1 : this.referenceIdPairs.length;
      this.referenceIdPairs.splice(insertIndex, 0, {
        '': ''
      });
      this.emitUpdate();
    },
    removeEntry(index) {
      this.referenceIdPairs.splice(index, 1);
      this.emitUpdate();
    },
    emitUpdate() {
      this.$emit('update', cloneDeep(this.referenceIdPairs));
    },
    async updateLinkedReferenceIds() {
      this.linkedReferenceIds = [];
      if (this.referenceIdPairs.length === 0) {
        return;
      }
      this.loading = true;
      const referenceIds = this.referenceIdPairs.map(entry => {
        const key = Object.keys(entry)[0];
        const value = Object.values(entry)[0];
        return {
          name: key,
          value: value,
          linkedTwins: []
        };
      }).filter(item => item.name && item.value);
      if (referenceIds.length === 0) {
        this.loading = false;
        return;
      }
      this.linkedReferenceIds = referenceIds;
      await this.fetchAllLinkedTwins(this.linkedReferenceIds);
      this.loading = false;
    },
    async fetchAllLinkedTwins(referenceIds) {
      if (referenceIds.length === 0) {
        return;
      }
      try {
        const searchItemsList = referenceIds.map(refId => ({
          variable: 'ddrDestination',
          key: refId.name,
          search: refId.value
        }));
        const searchItems = [{
          or: searchItemsList
        }];
        const filters = [];
        if (this.tenantId) {
          filters.push({
            type: 'tenantIds',
            ids: [this.tenantId]
          });
        }
        const result = await digitalTwinApi.digitalTwinsFetch(this,
        // context
        undefined,
        // userId
        undefined,
        // scopeIds
        filters.length > 0 ? filters : undefined,
        // filters
        undefined,
        // options
        undefined,
        // orderBy
        undefined,
        // offset
        undefined,
        // limit
        searchItems,
        // searchItems
        false,
        // includeIdentifiers
        false,
        // includeProperties
        false // includeReferences
        );
        if (result && result.digitaltwins) {
          this.processLinkedTwins(result.digitaltwins, referenceIds);
        }
      } catch (error) {
        console.error('Error fetching linked twins:', error);
      }
    },
    processLinkedTwins(twins, referenceIds) {
      const twinsByRefId = {};
      referenceIds.forEach(refId => {
        const key = `${refId.name}:${refId.value}`;
        twinsByRefId[key] = [];
      });
      twins.forEach(twin => {
        var _twin$properties;
        const ddrDestinations = ((_twin$properties = twin.properties) === null || _twin$properties === void 0 ? void 0 : _twin$properties.ddrDestination) || twin.ddrDestination || {};
        const ddrTargetDestinations = twin['dynamical-data-routing-destination'] || [];
        referenceIds.forEach(refId => {
          const propMatch = typeof ddrDestinations === 'object' && ddrDestinations !== null && ddrDestinations[refId.name] === refId.value;
          let routingTargets = twin['dynamical-data-routing-target'] || [];
          if (!Array.isArray(routingTargets) && routingTargets) {
            routingTargets = [routingTargets];
          }
          const routingMatch = Array.isArray(routingTargets) && routingTargets.some(target => {
            if (typeof target === 'object' && target !== null) {
              return Object.entries(target).some(([targetKey, targetValue]) => {
                const matches = targetKey === refId.name && targetValue === refId.value;
                return matches;
              });
            }
            return false;
          });
          const destinationMatch = Array.isArray(ddrTargetDestinations) && ddrTargetDestinations.some(target => {
            if (typeof target === 'object' && target !== null) {
              return Object.entries(target).some(([targetKey, targetValue]) => {
                const matches = targetKey === refId.name && targetValue === refId.value;
                return matches;
              });
            }
            return false;
          });
          if (propMatch || routingMatch || destinationMatch) {
            const key = `${refId.name}:${refId.value}`;
            twinsByRefId[key].push(twin);
          }
        });
      });
      referenceIds.forEach(refId => {
        const key = `${refId.name}:${refId.value}`;
        const matchingTwins = twinsByRefId[key] || [];
        const index = this.linkedReferenceIds.findIndex(item => item.name === refId.name && item.value === refId.value);
        if (index !== -1) {
          this.$set(this.linkedReferenceIds[index], 'linkedTwins', matchingTwins);
        }
      });
    },
    async refreshAllLinkedTwins() {
      if (this.linkedReferenceIds.length === 0) {
        return;
      }
      this.loading = true;
      await this.fetchAllLinkedTwins(this.linkedReferenceIds);
      this.loading = false;
    }
  }
};